// src/components/translation-modules/ProductManager.js
import React, { useState, useEffect } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import {
  FaTrash,
  FaEdit,
  FaPlus,
  FaImage,
  FaSave,
  FaArrowLeft,
} from "react-icons/fa"
import TranslationModuleBase from "./TranslationModuleBase"
import ImageManager from "./ImageManager"

// Use the correct domain WITHOUT www
const API_BASE_URL = "https://salumidelnorcino.com"

const ProductManager = ({
  currentTranslations,
  otherTranslations,
  currentLang,
  secondaryLang,
  onUpdateTranslations,
  registerSubmoduleAction,
  clearSubmoduleAction,
}) => {
  const [products, setProducts] = useState([])
  const [isEditing, setIsEditing] = useState(false)
  const [currentProduct, setCurrentProduct] = useState(null)
  const [showImagePicker, setShowImagePicker] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [showAddNewForm, setShowAddNewForm] = useState(false)
  const [newProduct, setNewProduct] = useState({
    title: "",
    desc: "",
    ingredients: "",
    pairing: "",
    img: "",
    badge: "",
    tags: [],
  })
  const [newTag, setNewTag] = useState("")

  // Extract products from translations
  useEffect(() => {
    if (
      currentTranslations &&
      currentTranslations.home &&
      currentTranslations.home.prodotti &&
      currentTranslations.home.prodotti.items
    ) {
      setProducts(currentTranslations.home.prodotti.items)
    }
  }, [currentTranslations])

  // Register and clear submodule actions when editing states change
  useEffect(() => {
    if (isEditing && currentProduct) {
      registerSubmoduleAction(
        handleUpdateProduct,
        `Product: ${currentProduct.title}`
      )
    } else if (showAddNewForm) {
      registerSubmoduleAction(saveNewProduct, "New Product")
    } else {
      clearSubmoduleAction()
    }
  }, [isEditing, currentProduct, showAddNewForm])

  const handleDragEnd = (result) => {
    if (!result.destination) return

    const items = Array.from(products)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    setProducts(items)
    saveProducts(items)
  }

  const saveProducts = (updatedProducts) => {
    // Update both languages
    const updatedCurrentTranslations = {
      ...currentTranslations,
      home: {
        ...currentTranslations.home,
        prodotti: {
          ...currentTranslations.home.prodotti,
          items: updatedProducts,
        },
      },
    }

    // If products don't exist in secondary language, create them
    let updatedOtherTranslations = { ...otherTranslations }

    if (!updatedOtherTranslations.home) {
      updatedOtherTranslations.home = {}
    }

    if (!updatedOtherTranslations.home.prodotti) {
      updatedOtherTranslations.home.prodotti = {}
    }

    // Make sure otherTranslations has the same structure
    if (
      !updatedOtherTranslations.home.prodotti.items ||
      updatedOtherTranslations.home.prodotti.items.length !==
        updatedProducts.length
    ) {
      updatedOtherTranslations = {
        ...updatedOtherTranslations,
        home: {
          ...updatedOtherTranslations.home,
          prodotti: {
            ...updatedOtherTranslations.home.prodotti,
            items: updatedProducts.map((product, index) => {
              const existingProduct =
                updatedOtherTranslations.home?.prodotti?.items?.[index]
              return {
                ...product,
                title: existingProduct?.title || product.title,
                desc: existingProduct?.desc || product.desc,
                ingredients:
                  existingProduct?.ingredients || product.ingredients,
                pairing: existingProduct?.pairing || product.pairing,
                // Keep image, badge, and tags the same for both languages
              }
            }),
          },
        },
      }
    }

    onUpdateTranslations(updatedCurrentTranslations, updatedOtherTranslations)
  }

  const handleEditProduct = (product, index) => {
    setCurrentProduct({ ...product, index })
    setIsEditing(true)
  }

  const handleDeleteProduct = (index) => {
    setConfirmDelete(index)
  }

  const confirmDeleteProduct = () => {
    if (confirmDelete !== null) {
      const updatedProducts = [...products]
      updatedProducts.splice(confirmDelete, 1)
      setProducts(updatedProducts)
      saveProducts(updatedProducts)
      setConfirmDelete(null)
    }
  }

  const handleAddProduct = () => {
    setNewProduct({
      title: "",
      desc: "",
      ingredients: "",
      pairing: "",
      img: "",
      badge: "",
      tags: [],
    })
    setShowAddNewForm(true)
  }

  const saveNewProduct = () => {
    if (!newProduct.title || !newProduct.img) {
      alert("Title and image are required.")
      return
    }

    const updatedProducts = [...products, newProduct]
    setProducts(updatedProducts)
    saveProducts(updatedProducts)
    setShowAddNewForm(false)
  }

  const handleUpdateProduct = () => {
    if (!currentProduct) return

    const updatedProducts = [...products]
    updatedProducts[currentProduct.index] = {
      title: currentProduct.title,
      desc: currentProduct.desc,
      ingredients: currentProduct.ingredients,
      pairing: currentProduct.pairing,
      img: currentProduct.img,
      badge: currentProduct.badge,
      tags: currentProduct.tags,
    }

    setProducts(updatedProducts)
    saveProducts(updatedProducts)
    setIsEditing(false)
    setCurrentProduct(null)
  }

  const handleAddTag = (isNewProduct = false) => {
    if (!newTag.trim()) return

    if (isNewProduct) {
      setNewProduct({
        ...newProduct,
        tags: [...newProduct.tags, newTag.trim()],
      })
    } else {
      setCurrentProduct({
        ...currentProduct,
        tags: [...currentProduct.tags, newTag.trim()],
      })
    }

    setNewTag("")
  }

  const handleRemoveTag = (index, isNewProduct = false) => {
    if (isNewProduct) {
      const updatedTags = [...newProduct.tags]
      updatedTags.splice(index, 1)
      setNewProduct({
        ...newProduct,
        tags: updatedTags,
      })
    } else {
      const updatedTags = [...currentProduct.tags]
      updatedTags.splice(index, 1)
      setCurrentProduct({
        ...currentProduct,
        tags: updatedTags,
      })
    }
  }

  const handleImageSelected = (imagePath) => {
    if (isEditing && currentProduct) {
      setCurrentProduct({
        ...currentProduct,
        img: imagePath,
      })
    } else if (showAddNewForm) {
      setNewProduct({
        ...newProduct,
        img: imagePath,
      })
    }
    setShowImagePicker(false)
  }

  // Helper function to render image with correct domain
  const renderProductImage = (imagePath) => {
    if (!imagePath) return null

    const fullImagePath = imagePath.startsWith("/")
      ? `${API_BASE_URL}${imagePath}`
      : imagePath

    return (
      <img
        src={`${fullImagePath}?_=${new Date().getTime()}`}
        alt="Product preview"
        className="h-32 object-contain border rounded"
        onError={(e) => {
          e.target.onerror = null
          e.target.src =
            'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>'
        }}
      />
    )
  }

  return (
    <TranslationModuleBase title="Product Manager">
      {/* Products List View */}
      {!isEditing && !showAddNewForm && !showImagePicker && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">Homepage Products</h3>
            <button
              onClick={handleAddProduct}
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-sm transition-colors"
            >
              <FaPlus className="mr-1" /> Add Product
            </button>
          </div>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="products">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {products.length === 0 ? (
                    <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-blue-700 mb-4">
                        No products found. Click "Add Product" to create one.
                      </p>
                      <button
                        onClick={handleAddProduct}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center transition-colors"
                      >
                        <FaPlus className="mr-2" /> Add Product
                      </button>
                    </div>
                  ) : (
                    products.map((product, index) => (
                      <Draggable
                        key={`product-${index}`}
                        draggableId={`product-${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="border rounded-lg overflow-hidden bg-white flex shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="w-24 h-24 flex-shrink-0 bg-gray-100 border-r">
                              <img
                                src={
                                  product.img.startsWith("/")
                                    ? `${API_BASE_URL}${
                                        product.img
                                      }?_=${new Date().getTime()}`
                                    : product.img
                                }
                                alt={product.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null
                                  e.target.src =
                                    'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>'
                                }}
                              />
                            </div>
                            <div className="p-3 flex-grow">
                              <div className="flex justify-between">
                                <div>
                                  <h4 className="font-medium text-blue-800">
                                    {product.title}
                                    {product.badge && (
                                      <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                        {product.badge}
                                      </span>
                                    )}
                                  </h4>
                                  <p className="text-sm text-gray-600 line-clamp-1">
                                    {product.desc}
                                  </p>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {product.tags &&
                                      product.tags.map((tag, i) => (
                                        <span
                                          key={i}
                                          className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"
                                        >
                                          {tag}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() =>
                                      handleEditProduct(product, index)
                                    }
                                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit product"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProduct(index)}
                                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Delete product"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}

      {/* Edit Product Form */}
      {isEditing && currentProduct && !showImagePicker && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800 flex items-center">
              <FaEdit className="mr-2" /> Edit Product
            </h3>
            <button
              onClick={() => {
                setIsEditing(false)
                setCurrentProduct(null)
              }}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-100 transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Cancel
            </button>
          </div>

          {/* Current Language Form */}
          <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-800 border-b border-blue-200 pb-2 mb-3">
              {currentLang.toUpperCase()}
            </h4>

            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Title
              </label>
              <input
                type="text"
                value={currentProduct.title}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    title: e.target.value,
                  })
                }
                className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Description
              </label>
              <textarea
                value={currentProduct.desc}
                onChange={(e) =>
                  setCurrentProduct({ ...currentProduct, desc: e.target.value })
                }
                className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Ingredients
              </label>
              <textarea
                value={currentProduct.ingredients}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    ingredients: e.target.value,
                  })
                }
                className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Pairing
              </label>
              <textarea
                value={currentProduct.pairing}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    pairing: e.target.value,
                  })
                }
                className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
              />
            </div>
          </div>

          {/* Secondary Language Form */}
          <div className="mt-5 p-4 bg-red-50 rounded-lg border border-red-200 space-y-3">
            <h4 className="font-medium text-red-800 border-b border-red-200 pb-2 mb-3">
              {secondaryLang.toUpperCase()}
            </h4>

            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">
                Title
              </label>
              <input
                type="text"
                value={
                  otherTranslations?.home?.prodotti?.items?.[
                    currentProduct.index
                  ]?.title || ""
                }
                onChange={(e) => {
                  const updatedOther = { ...otherTranslations }
                  if (!updatedOther.home) updatedOther.home = {}
                  if (!updatedOther.home.prodotti)
                    updatedOther.home.prodotti = {}
                  if (!updatedOther.home.prodotti.items)
                    updatedOther.home.prodotti.items = [...products]

                  const updatedItems = [...updatedOther.home.prodotti.items]
                  if (!updatedItems[currentProduct.index])
                    updatedItems[currentProduct.index] = { ...currentProduct }
                  updatedItems[currentProduct.index] = {
                    ...updatedItems[currentProduct.index],
                    title: e.target.value,
                  }

                  updatedOther.home.prodotti.items = updatedItems
                  onUpdateTranslations(currentTranslations, updatedOther)
                }}
                className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">
                Description
              </label>
              <textarea
                value={
                  otherTranslations?.home?.prodotti?.items?.[
                    currentProduct.index
                  ]?.desc || ""
                }
                onChange={(e) => {
                  const updatedOther = { ...otherTranslations }
                  if (!updatedOther.home) updatedOther.home = {}
                  if (!updatedOther.home.prodotti)
                    updatedOther.home.prodotti = {}
                  if (!updatedOther.home.prodotti.items)
                    updatedOther.home.prodotti.items = [...products]

                  const updatedItems = [...updatedOther.home.prodotti.items]
                  if (!updatedItems[currentProduct.index])
                    updatedItems[currentProduct.index] = { ...currentProduct }
                  updatedItems[currentProduct.index] = {
                    ...updatedItems[currentProduct.index],
                    desc: e.target.value,
                  }

                  updatedOther.home.prodotti.items = updatedItems
                  onUpdateTranslations(currentTranslations, updatedOther)
                }}
                className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">
                Ingredients
              </label>
              <textarea
                value={
                  otherTranslations?.home?.prodotti?.items?.[
                    currentProduct.index
                  ]?.ingredients || ""
                }
                onChange={(e) => {
                  const updatedOther = { ...otherTranslations }
                  if (!updatedOther.home) updatedOther.home = {}
                  if (!updatedOther.home.prodotti)
                    updatedOther.home.prodotti = {}
                  if (!updatedOther.home.prodotti.items)
                    updatedOther.home.prodotti.items = [...products]

                  const updatedItems = [...updatedOther.home.prodotti.items]
                  if (!updatedItems[currentProduct.index])
                    updatedItems[currentProduct.index] = { ...currentProduct }
                  updatedItems[currentProduct.index] = {
                    ...updatedItems[currentProduct.index],
                    ingredients: e.target.value,
                  }

                  updatedOther.home.prodotti.items = updatedItems
                  onUpdateTranslations(currentTranslations, updatedOther)
                }}
                className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">
                Pairing
              </label>
              <textarea
                value={
                  otherTranslations?.home?.prodotti?.items?.[
                    currentProduct.index
                  ]?.pairing || ""
                }
                onChange={(e) => {
                  const updatedOther = { ...otherTranslations }
                  if (!updatedOther.home) updatedOther.home = {}
                  if (!updatedOther.home.prodotti)
                    updatedOther.home.prodotti = {}
                  if (!updatedOther.home.prodotti.items)
                    updatedOther.home.prodotti.items = [...products]

                  const updatedItems = [...updatedOther.home.prodotti.items]
                  if (!updatedItems[currentProduct.index])
                    updatedItems[currentProduct.index] = { ...currentProduct }
                  updatedItems[currentProduct.index] = {
                    ...updatedItems[currentProduct.index],
                    pairing: e.target.value,
                  }

                  updatedOther.home.prodotti.items = updatedItems
                  onUpdateTranslations(currentTranslations, updatedOther)
                }}
                className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows="2"
              />
            </div>
          </div>

          {/* Shared Fields (same for both languages) */}
          <div className="mt-6 space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-medium border-b border-gray-200 pb-2 mb-3">
              Common Fields
            </h4>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Badge (optional)
              </label>
              <input
                type="text"
                value={currentProduct.badge || ""}
                onChange={(e) =>
                  setCurrentProduct({
                    ...currentProduct,
                    badge: e.target.value,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                placeholder="e.g. New, Special, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Image
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={currentProduct.img}
                  onChange={(e) =>
                    setCurrentProduct({
                      ...currentProduct,
                      img: e.target.value,
                    })
                  }
                  className="flex-grow p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  readOnly
                />
                <button
                  onClick={() => setShowImagePicker(true)}
                  className="p-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  title="Browse images"
                >
                  <FaImage />
                </button>
              </div>
              {currentProduct.img && (
                <div className="mt-2">
                  {renderProductImage(currentProduct.img)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Tags
              </label>
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddTag()
                    }
                  }}
                  className="flex-grow p-2 border border-gray-300 rounded focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="Add a tag"
                />
                <button
                  onClick={() => handleAddTag()}
                  className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentProduct.tags &&
                  currentProduct.tags.map((tag, i) => (
                    <div
                      key={i}
                      className="flex items-center bg-gray-100 rounded px-2 py-1"
                    >
                      <span className="text-sm mr-1">{tag}</span>
                      <button
                        onClick={() => handleRemoveTag(i)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6 sticky bottom-0">
            <button
              onClick={() => {
                setIsEditing(false)
                setCurrentProduct(null)
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Cancel
            </button>
            <button
              onClick={handleUpdateProduct}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-md transition-colors"
            >
              <FaSave className="mr-2" /> Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Add New Product Form */}
      {showAddNewForm && !showImagePicker && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-green-800 flex items-center">
              <FaPlus className="mr-2" /> Add New Product
            </h3>
            <button
              onClick={() => setShowAddNewForm(false)}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-100 transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Cancel
            </button>
          </div>

          <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Title
              </label>
              <input
                type="text"
                value={newProduct.title}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, title: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Description
              </label>
              <textarea
                value={newProduct.desc}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, desc: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Ingredients
              </label>
              <textarea
                value={newProduct.ingredients}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, ingredients: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Pairing
              </label>
              <textarea
                value={newProduct.pairing}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, pairing: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Badge (optional)
              </label>
              <input
                type="text"
                value={newProduct.badge}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, badge: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="e.g. New, Special, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Image
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newProduct.img}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, img: e.target.value })
                  }
                  className="flex-grow p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  readOnly
                />
                <button
                  onClick={() => setShowImagePicker(true)}
                  className="p-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  title="Browse images"
                >
                  <FaImage />
                </button>
              </div>
              {newProduct.img && (
                <div className="mt-2">{renderProductImage(newProduct.img)}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Tags
              </label>
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddTag(true)
                    }
                  }}
                  className="flex-grow p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Add a tag"
                />
                <button
                  onClick={() => handleAddTag(true)}
                  className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {newProduct.tags &&
                  newProduct.tags.map((tag, i) => (
                    <div
                      key={i}
                      className="flex items-center bg-gray-100 rounded px-2 py-1"
                    >
                      <span className="text-sm mr-1">{tag}</span>
                      <button
                        onClick={() => handleRemoveTag(i, true)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6 sticky bottom-0">
            <button
              onClick={() => setShowAddNewForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Cancel
            </button>
            <button
              onClick={saveNewProduct}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-md transition-colors"
            >
              <FaSave className="mr-2" /> Add Product
            </button>
          </div>
        </div>
      )}

      {/* Image Picker */}
      {showImagePicker && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800 flex items-center">
              <FaImage className="mr-2" /> Select an Image
            </h3>
            <button
              onClick={() => setShowImagePicker(false)}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-100 transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Back to Form
            </button>
          </div>

          <ImageManager
            onSelectImage={handleImageSelected}
            selectedImage={isEditing ? currentProduct?.img : newProduct?.img}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-red-600">
              Confirm Deletion
            </h3>
            <p className="mb-4">
              Are you sure you want to delete this product? This action cannot
              be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProduct}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </TranslationModuleBase>
  )
}

export default ProductManager
